/**
 * designParameters.js
 *
 * Design parameters let a template expose a handful of engineering knobs
 * (coating thickness, electrode dimensions, areal loading…) and derive LCI
 * quantities from them, so a user explores a design space instead of hand-
 * editing inventory rows.
 *
 * The block lives on the template/project as `design_parameters`:
 *
 *   {
 *     enabled: true,
 *     title, description,
 *     params:    { key: { label, unit, min, max, step, default } },
 *     constants: { key: number },
 *     derived:   { key: "<expression>" },     // evaluated in declaration order
 *     display:   [ { key, label, unit, emphasis?, sub? } ],  // sub: part of the row above
 *     quantity_map: [ { process_id, input_name | product, derived_key } ],
 *     cost_map:     [ { process_id, input_name, derived_key, cost_source } ],  // optional
 *     project_map:  [ { field, derived_key } ],   // optional
 *     sources:   [ { cite, url?, used_for? } ]  // optional; shown as citations
 *     notes:     [ "what the sources do not cover", … ]  // optional
 *   }
 *
 * `derived` expressions are strings parsed by the whitelisted evaluator in
 * `expressionEvaluator.js` — never `eval`. Each expression may read any param,
 * any constant, and any *earlier* derived key.
 *
 * `quantity_map` addresses an LCI row by `input_name` (or `input_id`), not by
 * position: index-based mapping silently writes to the wrong row as soon as a
 * user adds, removes or reorders an inventory line. A mapping with `product`
 * instead writes the quantity of the process output with that material name.
 *
 * `cost_map` writes a derived price to an LCI row's `cost_per_unit`, for a
 * price that depends on the design (foil priced per m² becomes $/kg through the
 * foil's thickness). It writes only while the row's `cost_source` is still the
 * mapping's: a price the user or the price search set clears that field (see
 * `withCost`), so the design never overwrites it.
 *
 * `project_map` writes a derived value to a `project_info` field. Only the
 * fields in PROJECT_FIELDS may be targeted — a template must not be able to
 * rewrite arbitrary project settings.
 */

'use strict';

import { compileExpression } from './expressionEvaluator';

export const DESIGN_PARAMETERS_KEY = 'design_parameters';
export const DESIGN_PARAM_VALUES_KEY = 'design_param_values';

// project_info fields a template's project_map may write. Energy density is the
// one a design changes: it sets the per-kWh basis of every result.
export const PROJECT_FIELDS = ['energy_density_wh_per_kg'];

// Pre-generalization projects (and saved files in the wild) carry this instead.
const LEGACY_PARAMETERS_KEY = 'coating_parameters';
const LEGACY_PARAM_VALUES_KEY = 'coating_param_values';

/**
 * Formulas for the SeaLion oCVD coating template, which used to live as
 * hardcoded JavaScript in CoatingParametersPanel. Current templates carry their
 * own `derived` block; this map exists only so a project saved before the
 * generalization still computes the same numbers.
 */
const LEGACY_COATING_DERIVED = {
  util: 'utilization_pct / 100',
  t_cm: 'coating_thickness_nm * 1e-7',
  r_cm: 'particle_diameter_um * 1e-4 / 2',
  sa_per_g: '3 / (particle_density_g_cm3 * r_cm)',
  pedot_per_g_particles: 'sa_per_g * t_cm * pedot_density_g_cm3',
  particle_fraction: '1 / (1 + pedot_per_g_particles)',
  pedot_mass_raw: '1 - particle_fraction',
  edot_raw: 'pedot_mass_raw / util',
  moles_edot: 'edot_raw * 1000 / mw_edot',
  vocl3_raw: 'moles_edot * oxidant_molar_ratio * mw_vocl3 / 1000',
  batch_hours: '1 / throughput_kg_hr',
  active_material_kg: 'round(particle_fraction, 4)',
  edot_kg: 'round(edot_raw, 4)',
  vocl3_kg: 'round(vocl3_raw, 4)',
  reactor_kwh: 'round(batch_hours * reactor_power_kw, 2)',
  pedot_wt_pct: 'round(pedot_mass_raw * 100, 2)',
  pedot_mass_kg: 'round(pedot_mass_raw, 4)',
};

// The old panel hardcoded a 0.5 kW reactor base load; legacy blocks have no
// such constant, so supply it when migrating.
const LEGACY_COATING_CONSTANTS = { reactor_power_kw: 0.5 };

// ---------------------------------------------------------------------------
// Reading the block
// ---------------------------------------------------------------------------

/**
 * Return the normalized design parameter block for a project or template, or
 * null if it has none / is disabled. Legacy `coating_parameters` blocks are
 * migrated in place.
 */
export function getDesignParameters(source) {
  if (!source) return null;

  const modern = source[DESIGN_PARAMETERS_KEY];
  if (modern && modern.enabled) return normalizeBlock(modern);

  const legacy = source[LEGACY_PARAMETERS_KEY];
  if (legacy && legacy.enabled) return normalizeBlock(migrateLegacyBlock(legacy));

  return null;
}

function migrateLegacyBlock(legacy) {
  return {
    ...legacy,
    constants: { ...LEGACY_COATING_CONSTANTS, ...(legacy.constants || {}) },
    derived: legacy.derived || LEGACY_COATING_DERIVED,
  };
}

function normalizeBlock(block) {
  const params = block.params || {};
  const derived = block.derived || {};
  const quantityMap = (block.quantity_map || []).map(normalizeMapping);

  // Without an explicit display list, preview whatever the quantity map writes.
  const display = Array.isArray(block.display) && block.display.length
    ? block.display
    : quantityMap
        .filter(m => m.derived_key)
        .map(m => ({ key: m.derived_key, label: m.derived_key }));

  return {
    enabled: block.enabled !== false,
    title: block.title || 'Design Parameters',
    description: block.description || '',
    basis: block.basis || '',
    params,
    constants: block.constants || {},
    derived,
    display,
    quantity_map: quantityMap,
    cost_map: (block.cost_map || [])
      .filter(m => m && m.process_id && m.input_name && m.derived_key && m.cost_source),
    project_map: (block.project_map || []).filter(m => m && m.field && m.derived_key),
    sources: Array.isArray(block.sources)
      ? block.sources.filter(src => src && typeof src.cite === 'string')
      : [],
    notes: Array.isArray(block.notes) ? block.notes.filter(n => typeof n === 'string') : [],
  };
}

function normalizeMapping(mapping) {
  return {
    process_id: mapping.process_id,
    // `formula` was the legacy spelling of `derived_key`.
    derived_key: mapping.derived_key || mapping.formula || null,
    input_name: mapping.input_name || null,
    input_id: mapping.input_id || null,
    product: mapping.product || null,
    // Retained only so legacy projects keep resolving; never written by new templates.
    input_index: Number.isInteger(mapping.input_index) ? mapping.input_index : null,
  };
}

// ---------------------------------------------------------------------------
// Parameter values
// ---------------------------------------------------------------------------

/** Current slider values: saved project values where present, defaults otherwise. */
export function resolveParamValues(block, project) {
  const saved = project?.[DESIGN_PARAM_VALUES_KEY] || project?.[LEGACY_PARAM_VALUES_KEY] || {};
  const values = {};
  for (const [key, def] of Object.entries(block.params || {})) {
    const candidate = saved[key];
    values[key] = Number.isFinite(Number(candidate)) ? Number(candidate) : def.default;
  }
  return values;
}

// ---------------------------------------------------------------------------
// Derived quantities
// ---------------------------------------------------------------------------

/**
 * Evaluate every `derived` expression in declaration order.
 * Returns { values, errors } — a failed expression is skipped and reported
 * rather than throwing, so one bad formula cannot blank the whole panel.
 */
export function computeDerived(block, paramValues) {
  const scope = { ...(block.constants || {}), ...paramValues };
  const values = {};
  const errors = [];

  for (const [key, expression] of Object.entries(block.derived || {})) {
    try {
      const result = compileExpression(expression)(scope);
      values[key] = result;
      scope[key] = result;
    } catch (err) {
      errors.push({ key, expression, message: err.message });
    }
  }

  return { values, errors };
}

// ---------------------------------------------------------------------------
// Writing quantities back onto the inventory
// ---------------------------------------------------------------------------

function normalizeName(name) {
  return String(name ?? '').trim().toLowerCase();
}

/**
 * Locate the LCI row a mapping addresses. Exact name match wins; a
 * case/whitespace-insensitive match is the fallback so a template author's
 * capitalization drift does not silently break the link.
 * Returns the index, or -1.
 */
function findInputIndex(inputs, mapping) {
  if (mapping.input_id) {
    const byId = inputs.findIndex(input => input && input.id === mapping.input_id);
    if (byId !== -1) return byId;
  }

  if (mapping.input_name) {
    const exact = inputs.findIndex(input => input && input.name === mapping.input_name);
    if (exact !== -1) return exact;

    const target = normalizeName(mapping.input_name);
    const loose = inputs.findIndex(input => input && normalizeName(input.name) === target);
    if (loose !== -1) return loose;
  }

  // Legacy projects addressed rows positionally.
  if (mapping.input_index !== null && mapping.input_index < inputs.length) {
    return mapping.input_index;
  }

  return -1;
}

/**
 * Apply derived values to the project's LCI quantities.
 * Returns { processes, applied, unresolved } without mutating the input —
 * `unresolved` lists mappings whose target row or derived value was missing, so
 * the UI can say so instead of quietly writing nothing.
 */
export function applyDerivedToProcesses(project, block, derivedValues) {
  const processes = { ...(project?.processes || {}) };
  const applied = [];
  const unresolved = [];

  for (const mapping of block.quantity_map || []) {
    const process = processes[mapping.process_id];
    if (!process) {
      unresolved.push({ ...mapping, reason: `no process "${mapping.process_id}"` });
      continue;
    }

    const quantity = derivedValues[mapping.derived_key];
    if (!Number.isFinite(quantity)) {
      unresolved.push({ ...mapping, reason: `no derived value "${mapping.derived_key}"` });
      continue;
    }

    if (mapping.product) {
      const products = Array.isArray(process.products) ? [...process.products] : [];
      const target = normalizeName(mapping.product);
      const index = products.findIndex(p => p && normalizeName(p.material) === target);
      if (index === -1) {
        unresolved.push({ ...mapping, reason: `no product "${mapping.product}"` });
        continue;
      }
      products[index] = { ...products[index], quantity };
      processes[mapping.process_id] = { ...process, products };
      applied.push({ ...mapping, index, quantity });
      continue;
    }

    const inputs = Array.isArray(process.lci_inputs) ? [...process.lci_inputs] : [];
    const index = findInputIndex(inputs, mapping);
    if (index === -1) {
      unresolved.push({ ...mapping, reason: `no input "${mapping.input_name || mapping.input_id}"` });
      continue;
    }

    inputs[index] = { ...inputs[index], quantity };
    processes[mapping.process_id] = { ...process, lci_inputs: inputs };
    applied.push({ ...mapping, index, quantity });
  }

  for (const mapping of block.cost_map || []) {
    const price = designPrice(processes[mapping.process_id], mapping, derivedValues);
    if (!price) continue;
    const inputs = [...processes[mapping.process_id].lci_inputs];
    inputs[price.index] = { ...inputs[price.index], cost_per_unit: price.value };
    processes[mapping.process_id] = { ...processes[mapping.process_id], lci_inputs: inputs };
  }

  return { processes, applied, unresolved };
}

/**
 * The price a `cost_map` entry sets, as { index, value }, or null when the row
 * is gone, the value is not a positive number, or the row's price is no longer
 * the design's own. Rounded to 4 significant figures, as the template builders
 * write prices.
 */
function designPrice(process, mapping, derivedValues) {
  const inputs = Array.isArray(process?.lci_inputs) ? process.lci_inputs : [];
  const index = findInputIndex(inputs, mapping);
  const value = derivedValues[mapping.derived_key];
  if (index === -1 || !(Number.isFinite(value) && value > 0)) return null;
  if (inputs[index].cost_source !== mapping.cost_source) return null;
  return { index, value: Number(value.toPrecision(4)) };
}

/**
 * Apply `project_map` to project_info. Fields outside PROJECT_FIELDS are
 * ignored, as are derived values that are not finite numbers.
 */
export function applyDerivedToProjectInfo(project, block, derivedValues) {
  const projectInfo = { ...(project?.project_info || {}) };
  for (const { field, derived_key: key } of block.project_map || []) {
    if (!PROJECT_FIELDS.includes(field)) continue;
    const value = derivedValues[key];
    if (Number.isFinite(value)) projectInfo[field] = Math.round(value * 10) / 10;
  }
  return projectInfo;
}

function sameQuantity(a, b) {
  return Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) <= 1e-6 * Math.max(1, Math.abs(b));
}

/**
 * Whether the project's inventory already holds what the block derives. False
 * when a template ships its own baseline quantities (e.g. ecoinvent's cell
 * composition) that differ from the design at its default slider positions —
 * the panel must not claim the quantities are up to date in that case.
 */
export function isInventoryInSync(project, block, derivedValues) {
  const { applied } = applyDerivedToProcesses(project, block, derivedValues);
  for (const { process_id: pid, index, product, quantity } of applied) {
    const process = project.processes[pid];
    const row = product ? process.products[index] : process.lci_inputs[index];
    if (!sameQuantity(Number(row.quantity), quantity)) return false;
  }
  for (const mapping of block.cost_map || []) {
    const price = designPrice(project.processes?.[mapping.process_id], mapping, derivedValues);
    const row = price && project.processes[mapping.process_id].lci_inputs[price.index];
    if (price && Math.abs(Number(row.cost_per_unit) - price.value) > 5e-4 * price.value) return false;
  }
  const expectedInfo = applyDerivedToProjectInfo(project, block, derivedValues);
  for (const { field } of block.project_map || []) {
    if (!PROJECT_FIELDS.includes(field)) continue;
    if (!sameQuantity(Number(project?.project_info?.[field]), expectedInfo[field])) return false;
  }
  return true;
}

/** Project with recomputed quantities and the parameter values that produced them. */
export function applyDesignParameters(project, block, paramValues) {
  const { values: derived, errors } = computeDerived(block, paramValues);
  const { processes, applied, unresolved } = applyDerivedToProcesses(project, block, derived);
  const projectInfo = applyDerivedToProjectInfo(project, block, derived);

  return {
    project: {
      ...project,
      processes,
      ...(block.project_map?.length ? { project_info: projectInfo } : {}),
      [DESIGN_PARAM_VALUES_KEY]: { ...paramValues },
    },
    derived,
    errors,
    applied,
    unresolved,
  };
}
